import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  User, Heart, Settings, LogOut, ChevronRight, 
  Palette, ShoppingBag, Clock, Star, Bell, Shield, Eye,
  Calendar, Trash2, ArrowRight, Search, Sliders, Camera
} from "lucide-react";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState({
    totalAnalyses: 0,
    wishlistItems: 0,
    savedOutfits: 0,
    totalSearches: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [wishlistRes, historyRes] = await Promise.all([
        axios.get(`${API}/wishlist`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/analyses`, { withCredentials: true }).catch(() => ({ data: { analyses: [] } }))
      ]);

      const wishlist = wishlistRes.data.items || [];
      const history = historyRes.data.analyses || [];

      setWishlistItems(wishlist);
      setHistoryItems(history);
      
      setStats({
        totalAnalyses: history.length,
        wishlistItems: wishlist.length,
        savedOutfits: 0, // Future feature
        totalSearches: history.reduce((acc, item) => acc + (item.search_count || 0), 0)
      });

      // Generate recent activity from history and wishlist
      const activity = [
        ...history.slice(0, 3).map(h => ({
          type: 'analysis',
          title: `Analyzed ${h.detected_items?.length || 0} items`,
          time: h.created_at,
          icon: Palette
        })),
        ...wishlist.slice(0, 3).map(w => ({
          type: 'wishlist',
          title: `Added ${w.title?.substring(0, 30)}...`,
          time: w.created_at,
          icon: Heart
        }))
      ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 5);

      setRecentActivity(activity);
    } catch (error) {
      console.error("Failed to fetch profile data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openAnalysis = (analysis) => {
    navigate("/dashboard", { 
      state: { 
        savedAnalysis: {
          analysis_id: analysis.analysis_id,
          detected_items: analysis.detected_items,
          created_at: analysis.created_at
        }
      } 
    });
  };

  const deleteAnalysis = async (e, analysisId) => {
    e.stopPropagation();
    setDeletingId(analysisId);
    try {
      await axios.delete(`${API}/analyses/${analysisId}`, { withCredentials: true });
      setHistoryItems(prev => prev.filter(a => a.analysis_id !== analysisId));
      setStats(prev => ({ ...prev, totalAnalyses: prev.totalAnalyses - 1 }));
      toast.success("Analysis deleted");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const removeFromWishlist = async (itemId) => {
    try {
      await axios.delete(`${API}/wishlist/${itemId}`, { withCredentials: true });
      setWishlistItems(prev => prev.filter(item => item.id !== itemId));
      setStats(prev => ({ ...prev, wishlistItems: prev.wishlistItems - 1 }));
      toast.success("Removed from wishlist");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "preferences", label: "Preferences", icon: Sliders },
    { id: "wishlist", label: "Wishlist", icon: Heart },
    { id: "history", label: "Saved Palettes", icon: Palette },
    { id: "settings", label: "Settings", icon: Settings }
  ];

  // Shopping preferences state
  const [preferences, setPreferences] = useState({
    age: user?.age || null,
    gender: user?.gender || '',
    gender_preference: user?.gender_preference || '',
    preferred_categories: user?.preferred_categories || [],
    preferred_retailers: user?.preferred_retailers || [],
    budget_min: user?.budget_min || 0,
    budget_max: user?.budget_max || 500
  });
  const [savingPreferences, setSavingPreferences] = useState(false);

  const categoryOptions = [
    { id: 'tops', label: 'Tops' },
    { id: 'bottoms', label: 'Bottoms' },
    { id: 'dresses', label: 'Dresses' },
    { id: 'outerwear', label: 'Outerwear' },
    { id: 'shoes', label: 'Shoes' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'activewear', label: 'Activewear' }
  ];

  const retailerOptions = [
    'Amazon', 'Nordstrom', 'ASOS', 'Zara', 'H&M', 'Nike', 'Adidas',
    'Target', 'Walmart', 'Macy\'s', 'Bloomingdale\'s', 'Net-a-Porter',
    'Revolve', 'Shopbop', 'Farfetch', 'SSENSE'
  ];

  const toggleCategory = (categoryId) => {
    setPreferences(prev => ({
      ...prev,
      preferred_categories: prev.preferred_categories.includes(categoryId)
        ? prev.preferred_categories.filter(c => c !== categoryId)
        : [...prev.preferred_categories, categoryId]
    }));
  };

  const toggleRetailer = (retailer) => {
    setPreferences(prev => ({
      ...prev,
      preferred_retailers: prev.preferred_retailers.includes(retailer)
        ? prev.preferred_retailers.filter(r => r !== retailer)
        : [...prev.preferred_retailers, retailer]
    }));
  };

  const savePreferences = async () => {
    setSavingPreferences(true);
    try {
      await axios.put(`${API}/auth/profile`, preferences, { withCredentials: true });
      toast.success("Preferences saved!");
    } catch (error) {
      console.error("Failed to save preferences:", error);
      toast.error("Failed to save preferences");
    } finally {
      setSavingPreferences(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="animate-shimmer w-32 h-1 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }}>
      <div className="container py-8">
        {/* Profile Header */}
        <div className="card mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-24 h-24 rounded-full object-cover border-4"
                  style={{ borderColor: 'var(--accent-primary)' }}
                />
              ) : (
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center border-4"
                  style={{ backgroundColor: 'var(--accent-muted)', borderColor: 'var(--accent-primary)' }}
                >
                  <User className="w-10 h-10" style={{ color: 'var(--accent-primary)' }} />
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-h2 mb-1" style={{ color: 'var(--text-primary)' }}>
                {user?.name || 'User'}
              </h1>
              <p className="text-small text-secondary mb-4">{user?.email}</p>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap justify-center md:justify-start gap-6">
                <div className="text-center">
                  <p className="text-h3" style={{ color: 'var(--accent-primary)' }}>{stats.totalAnalyses}</p>
                  <p className="text-meta text-muted">Analyses</p>
                </div>
                <div className="text-center">
                  <p className="text-h3" style={{ color: 'var(--accent-primary)' }}>{stats.wishlistItems}</p>
                  <p className="text-meta text-muted">Saved Items</p>
                </div>
                <div className="text-center">
                  <p className="text-h3" style={{ color: 'var(--accent-primary)' }}>{stats.totalSearches}</p>
                  <p className="text-meta text-muted">Searches</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-3">
              <Link to="/dashboard" className="btn-primary">
                New Analysis
              </Link>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-small font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'shadow-sm' 
                  : 'hover:bg-black/5'
              }`}
              style={{
                backgroundColor: activeTab === tab.id ? 'var(--accent-primary)' : 'transparent',
                color: activeTab === tab.id ? 'var(--text-inverse)' : 'var(--text-secondary)'
              }}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Recent Activity */}
                <div className="card">
                  <h3 className="text-h3 mb-6" style={{ color: 'var(--text-primary)' }}>Recent Activity</h3>
                  {recentActivity.length > 0 ? (
                    <div className="space-y-4">
                      {recentActivity.map((activity, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-4 p-3 rounded-lg transition-colors hover:bg-black/5"
                        >
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: 'var(--accent-muted)' }}
                          >
                            <activity.icon className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                          </div>
                          <div className="flex-1">
                            <p className="text-small" style={{ color: 'var(--text-primary)' }}>{activity.title}</p>
                            <p className="text-meta text-muted">{formatRelativeDate(activity.time)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-small text-muted text-center py-8">No recent activity</p>
                  )}
                </div>

                {/* Recent Wishlist Items */}
                <div className="card">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-h3" style={{ color: 'var(--text-primary)' }}>Saved Items</h3>
                    <Link 
                      to="#" 
                      onClick={() => setActiveTab("wishlist")}
                      className="text-small flex items-center gap-1"
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      View All <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                  {wishlistItems.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {wishlistItems.slice(0, 3).map((item, index) => (
                        <div key={index} className="group">
                          <div 
                            className="aspect-square rounded-lg overflow-hidden mb-2"
                            style={{ backgroundColor: 'var(--surface-secondary)' }}
                          >
                            {item.image_url && (
                              <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <p className="text-small font-medium line-clamp-1" style={{ color: 'var(--text-primary)' }}>
                            {item.title}
                          </p>
                          <p className="text-meta text-muted">{item.price}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-small text-muted text-center py-8">No saved items yet</p>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Links */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Quick Links</h3>
                  <div className="space-y-2">
                    <Link 
                      to="/dashboard"
                      className="flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-black/5"
                    >
                      <Palette className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                      <span className="text-small" style={{ color: 'var(--text-primary)' }}>Start New Analysis</span>
                    </Link>
                    <button 
                      onClick={() => setActiveTab("wishlist")}
                      className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-black/5 text-left"
                    >
                      <Heart className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                      <span className="text-small" style={{ color: 'var(--text-primary)' }}>View Wishlist</span>
                    </button>
                    <button 
                      onClick={() => setActiveTab("history")}
                      className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-black/5 text-left"
                    >
                      <Palette className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                      <span className="text-small" style={{ color: 'var(--text-primary)' }}>View Saved Palettes</span>
                    </button>
                  </div>
                </div>

                {/* Stats Card */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Your Stats</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-small text-secondary">Color Analyses</span>
                      <span className="text-small font-medium" style={{ color: 'var(--text-primary)' }}>{stats.totalAnalyses}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-small text-secondary">Saved Items</span>
                      <span className="text-small font-medium" style={{ color: 'var(--text-primary)' }}>{stats.wishlistItems}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-small text-secondary">Product Searches</span>
                      <span className="text-small font-medium" style={{ color: 'var(--text-primary)' }}>{stats.totalSearches}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Wishlist Tab */}
          {activeTab === "wishlist" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-h2" style={{ color: 'var(--text-primary)' }}>
                  My Wishlist ({wishlistItems.length})
                </h2>
              </div>
              
              {wishlistItems.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {wishlistItems.map((item, index) => (
                    <div key={index} className="product-card group">
                      <div className="product-card-image">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} />
                        )}
                      </div>
                      <div className="product-card-content">
                        <h4 className="text-small font-medium line-clamp-2 mb-1" style={{ color: 'var(--text-primary)' }}>
                          {item.title}
                        </h4>
                        <p className="text-meta text-muted mb-2">{item.source}</p>
                        <p className="text-body font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                          {item.price}
                        </p>
                        <div className="flex items-center gap-2">
                          <a
                            href={item.affiliate_url || item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary flex-1 text-center text-small py-2"
                          >
                            View Item
                          </a>
                          <button
                            onClick={() => removeFromWishlist(item.id)}
                            className="p-2 rounded-md border transition-all hover:bg-red-50"
                            style={{ borderColor: 'var(--border-subtle)' }}
                          >
                            <Heart className="w-4 h-4 fill-current" style={{ color: 'var(--accent-primary)' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card text-center py-16">
                  <Heart className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 className="text-h3 mb-2" style={{ color: 'var(--text-primary)' }}>No saved items yet</h3>
                  <p className="text-small text-secondary mb-6">Items you save will appear here</p>
                  <Link to="/dashboard" className="btn-primary">
                    Start Exploring
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* History Tab - Saved Palettes (matches History page design) */}
          {activeTab === "history" && (
            <div>
              <div className="mb-8">
                <p className="text-meta uppercase tracking-widest mb-2" style={{ color: 'var(--accent-primary)' }}>
                  Your Collection
                </p>
                <h2 className="text-h2" style={{ color: 'var(--text-primary)' }}>
                  Saved Palettes ({historyItems.length})
                </h2>
                <p className="text-small text-secondary mt-2">
                  Your history of analyzed garments and extracted color palettes. Click any analysis to continue searching.
                </p>
              </div>
              
              {historyItems.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {historyItems.map((analysis, index) => (
                    <div
                      key={analysis.analysis_id || index}
                      onClick={() => openAnalysis(analysis)}
                      className="card group relative cursor-pointer hover:shadow-lg transition-all"
                    >
                      {/* Delete Button */}
                      <button
                        onClick={(e) => deleteAnalysis(e, analysis.analysis_id)}
                        disabled={deletingId === analysis.analysis_id}
                        className="absolute top-4 right-4 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all z-10"
                        style={{ backgroundColor: 'var(--surface-secondary)' }}
                      >
                        {deletingId === analysis.analysis_id ? (
                          <div className="animate-shimmer w-4 h-4 rounded"></div>
                        ) : (
                          <Trash2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                        )}
                      </button>

                      {/* Date */}
                      <div className="flex items-center gap-2 mb-6">
                        <Calendar className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                        <span className="text-meta text-muted">
                          {formatDate(analysis.created_at)}
                        </span>
                      </div>

                      {/* Detected Items */}
                      <div className="space-y-6">
                        {analysis.detected_items?.map((item, itemIndex) => (
                          <div 
                            key={itemIndex} 
                            className="pb-6 last:pb-0"
                            style={{ borderBottom: itemIndex < analysis.detected_items.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                          >
                            <span className="text-meta uppercase tracking-widest block mb-1" style={{ color: 'var(--accent-primary)' }}>
                              {item.category}
                            </span>
                            <h3 className="text-body font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
                              {item.name}
                            </h3>
                            
                            {/* Color Swatches */}
                            <div className="flex gap-2">
                              {item.colors?.slice(0, 4).map((color, colorIndex) => (
                                <div 
                                  key={colorIndex}
                                  className="w-10 h-10 rounded-lg transition-transform hover:scale-105"
                                  style={{ 
                                    backgroundColor: color.hex, 
                                    border: '1px solid var(--border-subtle)',
                                    boxShadow: 'var(--shadow-sm)'
                                  }}
                                  title={`${color.pantone_name || color.name} - ${color.hex}`}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Continue Search CTA */}
                      <div 
                        className="mt-6 pt-4 flex items-center justify-between"
                        style={{ borderTop: '1px solid var(--border-subtle)' }}
                      >
                        <span className="text-small flex items-center gap-2" style={{ color: 'var(--accent-primary)' }}>
                          <Search className="w-4 h-4" />
                          Continue searching
                        </span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: 'var(--accent-primary)' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card text-center py-16">
                  <div 
                    className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--surface-secondary)' }}
                  >
                    <Palette className="w-10 h-10" style={{ color: 'var(--text-muted)' }} />
                  </div>
                  <h3 className="text-h3 mb-3" style={{ color: 'var(--text-primary)' }}>No saved palettes yet</h3>
                  <p className="text-small text-secondary mb-6">
                    Start analyzing clothing images to build your palette collection.
                  </p>
                  <Link to="/dashboard" className="btn-primary">
                    Analyze First Image
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <div className="max-w-2xl">
              <h2 className="text-h2 mb-6" style={{ color: 'var(--text-primary)' }}>Shopping Preferences</h2>
              
              <div className="space-y-6">
                {/* Profile Picture */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Profile Picture</h3>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: 'var(--surface-secondary)' }}
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-8 h-8 text-muted" />
                      )}
                    </div>
                    <div>
                      <button className="btn-secondary text-sm">Upload Photo</button>
                      <p className="text-meta text-muted mt-1">JPG, PNG or GIF. Max 2MB</p>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Personal Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-small text-muted mb-2 block">Age</label>
                      <input
                        type="number"
                        value={preferences.age || ''}
                        onChange={(e) => setPreferences(prev => ({ ...prev, age: e.target.value ? parseInt(e.target.value) : null }))}
                        placeholder="Your age"
                        className="input w-full"
                        min={13}
                        max={120}
                      />
                    </div>
                    <div>
                      <label className="text-small text-muted mb-2 block">Gender</label>
                      <select
                        value={preferences.gender || ''}
                        onChange={(e) => setPreferences(prev => ({ ...prev, gender: e.target.value || null }))}
                        className="input w-full"
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="non-binary">Non-binary</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Gender Preference */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Gender Preference for Results</h3>
                  <p className="text-small text-muted mb-4">Choose which clothing results to show</p>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { id: 'womenswear', label: "Women's" },
                      { id: 'menswear', label: "Men's" },
                      { id: 'both', label: 'Both' }
                    ].map(option => (
                      <button
                        key={option.id}
                        onClick={() => setPreferences(prev => ({ ...prev, gender_preference: option.id }))}
                        className={`px-4 py-2 rounded-full text-small transition-all ${
                          preferences.gender_preference === option.id
                            ? 'text-white'
                            : 'border hover:border-accent-primary'
                        }`}
                        style={{
                          backgroundColor: preferences.gender_preference === option.id ? 'var(--accent-primary)' : 'transparent',
                          borderColor: preferences.gender_preference === option.id ? 'var(--accent-primary)' : 'var(--border-subtle)'
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preferred Categories */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Preferred Garment Categories</h3>
                  <p className="text-small text-muted mb-4">Select the types of clothing you're interested in</p>
                  <div className="flex flex-wrap gap-2">
                    {categoryOptions.map(category => (
                      <button
                        key={category.id}
                        onClick={() => toggleCategory(category.id)}
                        className={`px-4 py-2 rounded-full text-small transition-all ${
                          preferences.preferred_categories.includes(category.id)
                            ? 'text-white'
                            : 'border hover:border-accent-primary'
                        }`}
                        style={{
                          backgroundColor: preferences.preferred_categories.includes(category.id) ? 'var(--accent-primary)' : 'transparent',
                          borderColor: preferences.preferred_categories.includes(category.id) ? 'var(--accent-primary)' : 'var(--border-subtle)'
                        }}
                      >
                        {category.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preferred Retailers */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Preferred Retailers</h3>
                  <p className="text-small text-muted mb-4">We'll prioritize results from your favorite stores</p>
                  <div className="flex flex-wrap gap-2">
                    {retailerOptions.map(retailer => (
                      <button
                        key={retailer}
                        onClick={() => toggleRetailer(retailer)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          preferences.preferred_retailers.includes(retailer)
                            ? 'text-white'
                            : 'border hover:border-accent-primary'
                        }`}
                        style={{
                          backgroundColor: preferences.preferred_retailers.includes(retailer) ? 'var(--accent-primary)' : 'transparent',
                          borderColor: preferences.preferred_retailers.includes(retailer) ? 'var(--accent-primary)' : 'var(--border-subtle)'
                        }}
                      >
                        {retailer}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Budget Range */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Budget Range</h3>
                  <p className="text-small text-muted mb-4">Set your preferred price range for product results</p>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-small" style={{ color: 'var(--text-secondary)' }}>
                        ${preferences.budget_min} - ${preferences.budget_max}+
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-muted mb-1 block">Minimum</label>
                        <input
                          type="range"
                          min="0"
                          max="500"
                          step="10"
                          value={preferences.budget_min}
                          onChange={(e) => setPreferences(prev => ({ 
                            ...prev, 
                            budget_min: Math.min(parseInt(e.target.value), prev.budget_max - 10) 
                          }))}
                          className="w-full accent-amber-600"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted mb-1 block">Maximum</label>
                        <input
                          type="range"
                          min="0"
                          max="1000"
                          step="10"
                          value={preferences.budget_max}
                          onChange={(e) => setPreferences(prev => ({ 
                            ...prev, 
                            budget_max: Math.max(parseInt(e.target.value), prev.budget_min + 10) 
                          }))}
                          className="w-full accent-amber-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <button 
                  onClick={savePreferences}
                  disabled={savingPreferences}
                  className="btn-primary w-full"
                >
                  {savingPreferences ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="max-w-2xl">
              <h2 className="text-h2 mb-6" style={{ color: 'var(--text-primary)' }}>Settings</h2>
              
              <div className="space-y-6">
                {/* Account Section */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Account</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                        <div>
                          <p className="text-small" style={{ color: 'var(--text-primary)' }}>Name</p>
                          <p className="text-meta text-muted">{user?.name || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                        <div>
                          <p className="text-small" style={{ color: 'var(--text-primary)' }}>Email</p>
                          <p className="text-meta text-muted">{user?.email || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preferences Section */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Preferences</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                        <div>
                          <p className="text-small" style={{ color: 'var(--text-primary)' }}>Email Notifications</p>
                          <p className="text-meta text-muted">Get updates about price drops</p>
                        </div>
                      </div>
                      <button 
                        className="px-4 py-1.5 rounded-md text-small"
                        style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}
                      >
                        Coming Soon
                      </button>
                    </div>
                  </div>
                </div>

                {/* Privacy Section */}
                <div className="card">
                  <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>Privacy & Data</h3>
                  <div className="space-y-4">
                    <Link 
                      to="/privacy"
                      className="flex items-center justify-between py-3 hover:bg-black/5 -mx-4 px-4 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                        <span className="text-small" style={{ color: 'var(--text-primary)' }}>Privacy Policy</span>
                      </div>
                      <ChevronRight className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                    </Link>
                    <Link 
                      to="/terms"
                      className="flex items-center justify-between py-3 hover:bg-black/5 -mx-4 px-4 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                        <span className="text-small" style={{ color: 'var(--text-primary)' }}>Terms of Service</span>
                      </div>
                      <ChevronRight className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
